import {
  Button,
  ConfigProvider,
  Flex,
  Dropdown,
  Spin,
  Menu,
  TabsProps,
  Modal,
  Form,
  Input,
  Select,
  Tooltip,
  notification,
} from "antd";
import InsigthButton from "components/Common/Button/InsigthButton";
import { Title3 } from "components/Common/Typography/Titles";
import {
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { SvgIcons } from "Icon/SvgIcons";
import arrowdown from "../../images/arrowdown.svg";
import { FaCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  updateProjectStatus,
  duplicateProject,
  deleteProject,
  fetchProjectContributors,
  fetchAllContributors,
  addProjectContributors,
} from "api/project.api";
import { setActiveStep } from "store/slices/searcSlice";
import { getStatusObject } from "utils/utils";
import { useIsOldFlow } from "services/CustomeHooks";
const FaCircleSin = FaCircle as unknown as React.FC as any;
type StatusType = "OPEN" | "ON_HOLD" | "COMPLETE" | "CANCELLED";

interface ProjectSavedSearchesHeaderProps {
  selectedPool: number;
  getLastTab: () => number | null; // Function that returns a number
  popTab: () => void; // Function that pops the last tab, returns nothing
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  setState: React.Dispatch<React.SetStateAction<string>>;
}

const items: TabsProps["items"] = [
  {
    key: "1",
    label: (
      <a target="_blank" rel="noopener noreferrer" href="https://www.antgroup.com">
        1st menu item
      </a>
    ),
  },
  {
    key: "2",
    label: (
      <a target="_blank" rel="noopener noreferrer" href="https://www.aliyun.com">
        2nd menu item
      </a>
    ),
  },
  {
    key: "3",
    label: (
      <a target="_blank" rel="noopener noreferrer" href="https://www.luohanacademy.com">
        3rd menu item
      </a>
    ),
  },
];

type Contributor = {
  id: number;
  name: string;
  email: string;
};

const ProjectSavedSearchesHeader: React.FC<ProjectSavedSearchesHeaderProps> = ({
  selectedPool,
  getLastTab,
  popTab,
  currentIndex,
  setCurrentIndex,
  setState,
}) => {
  const navigate = useNavigate();
  const projectDetails = useSelector((state: any) => state.projectSlice?.selected_project || {});
  const previousIndex = getLastTab();
  const [addContributor, setAddContributor] = useState(false);
  // Assuming the initial status is from the projectDetails
  const [status, setStatus] = useState<StatusType>("OPEN");
  const [loading, setLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [projectContributors, setProjectContributors] = useState<Contributor[]>([]);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [addedContributor, setAddedContributor] = useState<Contributor[]>([]);
  const [selectedContributors, setSelectedContributors] = useState<Contributor | null>(null);
  // const [addContributorLoading, setAddContributorLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const dispatch = useDispatch();
  const isAddContributorsAllowed = projectDetails.actions_allowed?.add_contributors;
  const isOldSearchFlow = useIsOldFlow();
  const [form] = Form.useForm();

  const handleBackTab = () => {
    if (currentIndex !== previousIndex) {
      popTab();
      if (previousIndex === 1) {
        setCurrentIndex(1);
        navigate(`/projects/analytics/${selectedPool}`);
      } else if (previousIndex === 2) {
        setCurrentIndex(2);
        navigate(`/projects/saved-searches/${selectedPool}`);
      } else if (previousIndex === 3) {
        setCurrentIndex(3);
        navigate(`/projects/recent-searches/${selectedPool}`);
      } else if (previousIndex === 4) {
        setCurrentIndex(4);
        navigate(`/projects/talent-pool/${selectedPool}`);
      } else if (previousIndex === 99) {
        setCurrentIndex(99);
        navigate(`/projects/details/${selectedPool}`);
      } else if (!previousIndex) {
        navigate(`/projects/`);
      }
    }
  };

  const fetchAllContributor = async () => {
    try {
      setLoading(true);
      const response: any = await fetchAllContributors(selectedPool);
      setContributors(response); // Store response directly
    } catch (error) {
      console.error("Error fetching contributor list:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        console.log("Contributor Email:", values.email);
        setAddContributor(false);
        form.resetFields(); // Reset form after submission
      })
      .catch((errorInfo) => {
        console.error("Validation Failed:", errorInfo);
      });
  };

  const handleCancel = () => {
    setAddContributor(false);
    form.resetFields();
  };
  const menu = (
    <Menu>
      <Menu.Item key="OPEN" onClick={() => handleStatusChange("OPEN")}>
        Open
      </Menu.Item>
      <Menu.Item key="ON_HOLD" onClick={() => handleStatusChange("ON_HOLD")}>
        On Hold
      </Menu.Item>
      <Menu.Item key="COMPLETE" onClick={() => handleStatusChange("COMPLETE")}>
        Completed
      </Menu.Item>
      <Menu.Item key="CANCELLED" onClick={() => handleStatusChange("CANCELLED")}>
        Cancelled
      </Menu.Item>
    </Menu>
  );

  const getStatusColor = (status: StatusType) => {
    switch (status) {
      case "OPEN":
        return "green";
      case "ON_HOLD":
        return "orange";
      case "COMPLETE":
        return "blue";
      case "CANCELLED":
        return "red";
      default:
        return "gray";
    }
  };

  const handleStatusChange = async (newStatus: StatusType) => {
    try {
      setLoading(true);
      // Call the API to update project status
      const response = await updateProjectStatus(newStatus, selectedPool);
      // Update local state if the API call is successful
      setStatus(newStatus);
      setState(newStatus);
    } catch (error) {
      console.error("Error updating project status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateProject = async (id: number) => {
    try {
      setCopyLoading(true);
      const response: any = await duplicateProject(id);
      navigate("/projects");
    } catch (error) {
      console.error("Error fetching pipeline list:", error);
    } finally {
      setCopyLoading(false);
    }
  };

  const handleDeletingProject = async (id: number) => {
    try {
      setDeleteLoading(true);
      const response: any = await deleteProject(id);
      navigate("/projects");
    } catch (error) {
      console.error("Error fetching pipeline list:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAddContributor = async () => {
    if (selectedContributors) {
      // Wait for form validation
      const { email } = await form.validateFields();
      const contributorId: number | undefined = selectedContributors?.id;
      if (!contributorId) {
        console.error("No contributor ID is selected.");
        return; // Exit if there's no valid contributor ID
      }
      try {
        setLoading(true);
        const response = await addProjectContributors([selectedPool], [contributorId]);
        setAddedContributor((prev) => [...prev, selectedContributors]);
        // Reset the form and toggle modals
        form.resetFields();
        setIsModalVisible(false);
        notification.success({
          message: "Successful",
          description: "Contributor was successfully added.",
        });
      } catch (errorInfo: any) {
        console.error("Validation Failed or Async Error:", errorInfo);
        notification.error({
          message: "Failed",
          description: `${errorInfo.message} || Failed to add contributor!`,
        });
      } finally {
        setLoading(false);
      }
    } else {
      console.error("No contributor selected to add.");
    }
  };

  const fetchProjectContributor = async () => {
    try {
      setLoading(true);
      const response: any = await fetchProjectContributors(selectedPool);
      setProjectContributors(response.results); // Store response directly
    } catch (error) {
      console.error("Error fetching contributor list:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewSearch = () => {
    if (isOldSearchFlow) {
      dispatch(setActiveStep(1));
      navigate(`/?job-id=${selectedPool}`);
    } else {
      navigate(`/hr-flow/search?searchType=rpo&step=1&projectId=${selectedPool}`);
    }
  };

  useEffect(() => {
    fetchAllContributor();
    // fetchProjectContributor();
  }, [selectedPool, addedContributor]);

  useEffect(() => {
    fetchProjectContributor();
  }, [addedContributor]);

  return (
    <>
      <Flex justify="space-between" className="pr-4 h-[51px] border-b bg-white" align="center">
        <div className="flex gap-2 items-center">
          <span
            onClick={handleBackTab}
            style={{ cursor: currentIndex === previousIndex ? "" : "pointer" }}
            className="w-9 h-9 p-2 flex justify-between items-center rounded-r-lg cursor-pointer "
          >
            {SvgIcons.arrowNarrowLeft}
          </span>
          <p className="text-text-heading font-bold text-xl">
            Projects: &nbsp;
            <span className="text-xl font-semibold text-text-secondary capitalize">
              {projectDetails?.title || projectDetails?.project_name}
            </span>
          </p>
          <Dropdown overlay={menu} trigger={["click"]}>
            <Spin spinning={loading}>
              <button className="py-1 px-3 bg-bg-secondary flex items-center gap-2 border border-main-border rounded-md">
                <FaCircleSin
                  size={8}
                  style={{ color: getStatusObject(projectDetails?.status)?.color }}
                />
                {getStatusObject(projectDetails?.status)?.label}
                <img src={arrowdown} alt="Arrow down" />
              </button>
            </Spin>
          </Dropdown>
        </div>
        <Flex align="center" gap={24}>
          <Spin spinning={copyLoading}>
            <Tooltip title="Duplicate Project" placement="top" overlayClassName="custom-tooltip">
              <button onClick={() => handleDuplicateProject(projectDetails.id)}>
                {SvgIcons.copySvg}
              </button>
            </Tooltip>
          </Spin>
          <Spin spinning={deleteLoading}>
            <Tooltip title="Delete Project" placement="top" overlayClassName="custom-tooltip">
              <button onClick={() => handleDeletingProject(projectDetails.id)}>
                {SvgIcons.deleteSvg}
              </button>
            </Tooltip>
          </Spin>
          {/* <Button color="#845dda" type="dashed" className="relative font-semibold text-sm text-purple py-2 px-3 border border-[#875F9A] flex gap-1 rounded-lg group">
                        <PlusOutlined />
                        Add contributor
                    </Button> */}
          {/* <Button color="#845dda" type="dashed" onClick={() => setAddContributor(true)} className="relative font-semibold text-sm text-purple py-2 px-3 border border-[#875F9A] flex gap-1 rounded-lg group">
                        <PlusOutlined />
                        Add contributor
                    </Button> */}
          {/* <Button
            type="dashed"
            onClick={() => setAddContributor(true)}
            className="relative font-semibold text-sm text-purple py-2 px-3 border border-[#875F9A] flex gap-1 rounded-lg group"
          >
            <PlusOutlined />
            Add contributor
          </Button> */}
          <Tooltip
            title={isAddContributorsAllowed ? "" : "You don't have permission to add contributors"}
            placement="top"
            overlayClassName="custom-tooltip"
          >
            <Button
              type="dashed"
              onClick={() => setAddContributor(true)}
              className="relative font-semibold text-sm text-purple py-2 px-3 border border-[#875F9A] flex gap-1 rounded-lg group"
              disabled={!isAddContributorsAllowed}
            >
              <PlusOutlined />
              Add Contributor
            </Button>
          </Tooltip>
          <Button
            className="relative font-semibold text-sm text-white py-2 px-3 bg-[#845dda] border border-[#7F56D9] flex gap-1 rounded-lg group"
            onClick={handleNewSearch}
          >
            <SearchOutlined />
            Start New Search
          </Button>
        </Flex>
      </Flex>
      <Modal
        title="Add Contributor"
        visible={addContributor}
        onCancel={handleCancel}
        footer={null} // Remove default footer
        centered
        width={300} // Decrease modal width
      >
        <Form form={form} layout="vertical" name="addContributorForm">
          <Form.Item
            label={<span style={{ color: "darkgray" }}>Contributor E-mail</span>}
            name="email"
            rules={[{ required: true, message: "Please select a contributor email!" }]}
          >
            <Select
              placeholder="Select a contributor email"
              showSearch
              optionFilterProp="children"
              onChange={(value) => {
                const selectedContributor = contributors.find(
                  (contributor) => contributor.email === value
                );
                selectedContributor && setSelectedContributors(selectedContributor); // Update the state with the selected contributor
              }}
            >
              {contributors.map((contributor) => (
                <Select.Option
                  key={contributor.id}
                  value={contributor.email}
                  style={{
                    display: "flex", // Use flexbox for row alignment
                    justifyContent: "space-between", // Align name and email with space between
                    alignItems: "center",
                    padding: "8px 12px",
                    borderBottom: "1px solid #e0e0e0", // Divider between options
                  }}
                >
                  <span>{contributor.name}</span>
                  <span className="mx-1" style={{ color: "gray", fontSize: "12px" }}>
                    {contributor.email}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div
            style={{
              display: "flex",
              justifyContent: "center", // Center align the buttons
              gap: "10px", // Add spacing between buttons
              marginTop: "20px", // Add spacing above buttons
            }}
          >
            <Button
              loading={loading}
              onClick={handleCancel}
              style={{ borderRadius: 4, width: "120px" }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={loading}
              onClick={handleAddContributor}
              style={{ backgroundColor: "#7A5AF8", borderRadius: 4, width: "120px" }}
            >
              Save
            </Button>
          </div>
        </Form>
      </Modal>
      {/* <Modal
        visible={isSuccessModalVisible}
        onCancel={() => setIsSuccessModalVisible(false)}
        footer={null}
        centered
        width={400}
      >
        <div style={{ textAlign: "center" }}>
          <CheckCircleOutlined
            style={{ color: "#52c41a", fontSize: "40px", marginBottom: "20px" }}
          />
          <div style={{ fontSize: "18px", fontWeight: "bold" }}>
            Contributor Was Successfully Added
          </div>
        </div>
      </Modal> */}
    </>
  );
};

export default ProjectSavedSearchesHeader;
